"use client";

import { useIsDesktop } from "@/hooks/layout/use-is-desktop";
import { useToggle } from "@/hooks/use-toggle";
import { useAuthDialogContext } from "@/providers/auth-dialog-provider";
import { useFollowsContext } from "@/providers/follows-provider";
import { UserIcon, UserPlus2Icon } from "lucide-react";
import { useSession } from "next-auth/react";
import { OnboardingItem } from "../onboarding/onboarding-item";
import { onboardingConfig } from "../onboarding/onboarding-provider";
import { RemoveDialog } from "../portfolios/portfolio/dialogs/remove-dialog";
import { Button, ButtonProps } from "../ui/button";

interface Props {
  readonly onFollowCb?: () => void;
  readonly size?: ButtonProps["size"];
}

export function UserFollow({ onFollowCb, size }: Props) {
  const isDesktop = useIsDesktop();
  const { data: session } = useSession();
  const followsContext = useFollowsContext();
  const authDialogContext = useAuthDialogContext();

  const handleFollow = async () => {
    if (!session) {
      authDialogContext.onOpenChange(true);
      return;
    }
    followsContext.updateFollowing();
    onFollowCb?.();
  };

  if (followsContext.isSameUser) {
    return null;
  }

  return (
    <>
      {/* Allow to follow only portfolios with visible author  */}
      {followsContext.isFollowAllowed ? <FollowButton
          handleFollow={handleFollow}
          sessionUser={session?.user ?? null}
          size={size}
          textHidden={!isDesktop}
        /> : null}
    </>
  );
}

interface FollowButtonProps {
  readonly handleFollow: () => void;
  readonly size?: ButtonProps["size"];
  readonly sessionUser:
    | NonNullable<ReturnType<typeof useSession>["data"]>["user"]
    | null;
  readonly textHidden?: boolean;
}

function FollowButton(props: FollowButtonProps) {
  const { data: session } = useSession();
  const follows = useFollowsContext();
  const unfollowUserToggle = useToggle();

  if (follows.isFollowing) {
    return (
      <RemoveDialog
        title={"Unfollow user"}
        description={`Are you sure you want to unfollow ${
          follows.user?.name ? `"${follows.user?.name}"` : "this user"
        }?`}
        cancel={{
          text: "Cancel",
          onClick: () => {},
        }}
        confirm={{ text: "Unfollow", onClick: props.handleFollow }}
      >
        <Button
          variant={"default"}
          size={props.size}
          onClick={unfollowUserToggle.setOn}
          gtmEvent={{
            event: "click",
            eventCategory: "social",
            eventLabel: "user-unfollow",
            eventAction: "unfollow",
            userId: props.sessionUser?.id,
            followerId: props.sessionUser?.id,
            followingId: follows.followingId,
          }}
          className="gap-2"
        >
          <UserIcon className="size-4" />
          {props.textHidden ? "" : "Following"}
        </Button>
      </RemoveDialog>
    );
  }

  return (
    <OnboardingItem
      targetId={"onboarding-1-user-profile-step-1"}
      content={onboardingConfig.followUser}
    >
      <Button
        variant={"ghost"}
        size={props.size}
        onClick={props.handleFollow}
        gtmEvent={{
          event: "click",
          eventCategory: "social",
          eventLabel: "user-follow",
          eventAction: "follow",
          userId: session?.user?.id,
          followerId: session?.user?.id,
          followingId: follows.followingId,
        }}
        className="gap-2"
      >
        <UserPlus2Icon className="size-4" />
        {props.textHidden ? "" : "Follow"}
      </Button>
    </OnboardingItem>
  );
}
